import * as React from 'react';

import {
    AchatsAppDispatch,
    AchatsStateType,
    CodeRejet,
    CodeRejetApiObject,
    useAchatsService,
} from '@europrocurement/l2d-domain';
import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import { DataSource, getDataThunkType } from '@europrocurement/l2d-redux-utils';

import {
    DEF_FREQUENCE_RELANCE_ACHATS_DATASOURCE_NAME,
    DefFrequenceApiObject,
} from '@europrocurement/l2d-domain/reducers/achats/slices/defFrequenceSlice';
import { useDispatch, useSelector } from 'react-redux';
import { usePatchRejectCode } from '../../../../hooks/rejectCodeHooks';
import AsyncSwitchRejectCodeRelanceAuto from '../fragments/AsyncSwitchRejectCodeRelanceAuto';
import AsyncSwitchRejectCodeBloquant from '../fragments/AsyncSwitchRejectCodeBloquant';
import { FrequencySelector } from '../fragments/FrequencySelector';

/**
 * Defines the properties for the Reject Codes List Component
 */
export type RejectCodesListComponentProps = {
    dataSource: DataSource<CodeRejet>;
    fetchData: getDataThunkType<CodeRejetApiObject>;
    disabledToggles?: Partial<Record<'manuel' | 'bloquant' | 'relanceAuto', boolean>>;
};

/**
 * Component for displaying and managing a list of reject codes
 *
 * @component
 * @param {RejectCodesListComponentProps} props - Component properties
 */
const RejectCodesListComponent: React.FunctionComponent<RejectCodesListComponentProps> = function ({
    dataSource,
    fetchData,
    disabledToggles,
}) {
    const dispatch = useDispatch<AchatsAppDispatch>();
    const patchRejectCode = usePatchRejectCode();

    const frequencyRelanceAchatsDatasource = useSelector<AchatsStateType>(
        (s) => s.achats.frequence[DEF_FREQUENCE_RELANCE_ACHATS_DATASOURCE_NAME],
    ) as DataSource<DefFrequenceApiObject>;

    const { defFrequenceDataSourcesThunks } = useAchatsService();

    const frequencyRelanceAchatsFetchData =
        defFrequenceDataSourcesThunks[DEF_FREQUENCE_RELANCE_ACHATS_DATASOURCE_NAME].data
            .fetchRelanceAchatsFrequences;

    // Memoized columns configuration
    const columns: ColumnDatatable<CodeRejet>[] = React.useMemo(
        () => [
            {
                label: 'Code',
                isDisplayed: true,
                render: ({ code }) => code,
                field: 'code',
            },
            {
                label: 'Libelle',
                isDisplayed: true,
                render: ({ libelle }) => libelle,
                field: 'libelle',
            },
            {
                label: 'Litige CEGID',
                isDisplayed: true,
                onClickCell: () => {},
                render: (rejectCode) => (
                    <AsyncSwitchRejectCodeBloquant
                        fetchData={fetchData}
                        rejectCode={rejectCode}
                        disabledToggles={disabledToggles}
                    />
                ),
            },
            {
                label: 'Relance active',
                isDisplayed: true,
                onClickCell: () => {},
                render: (rejectCode) => (
                    <AsyncSwitchRejectCodeRelanceAuto
                        fetchData={fetchData}
                        rejectCode={rejectCode}
                        disabled={disabledToggles?.relanceAuto}
                    />
                ),
            },
            {
                label: 'Fréquence de relance',
                isDisplayed: true,
                onClickCell: () => {},
                render: (rejectCode) => (
                    <FrequencySelector
                        variant="codeRejet"
                        codeRejet={rejectCode}
                        fetchCodeRejetData={fetchData}
                        patchCodeRejet={patchRejectCode}
                        frequencyDatasource={frequencyRelanceAchatsDatasource}
                        fetchFrequencies={() => dispatch(frequencyRelanceAchatsFetchData({}))}
                    />
                ),
            },
            {
                label: 'Actions',
                render: () => <> </>,
                isDisplayed: true,
            },
        ],
        [
            disabledToggles,
            dispatch,
            fetchData,
            frequencyRelanceAchatsDatasource,
            frequencyRelanceAchatsFetchData,
            patchRejectCode,
        ],
    );

    // Empty filters array
    const filters: FiltersDatatableList = [];

    return (
        <StoreDatatable
            dataSource={dataSource}
            columns={columns}
            fetchData={fetchData}
            filters={filters}
            localStorageKey="FlexyStoreDatatableRejectCodes"
            localStorageRefreshDate={new Date('2023-06-23T09:42:00.000Z')}
            fetchWithoutFilter
        />
    );
};

export default RejectCodesListComponent;
